import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PermissionGate from './PermissionGate';
import SolicitacaoForm from './SolicitacaoForm';
import PedidoEnviado from './PedidoEnviado';
import type { Solicitacao } from '../services/solicitacaoService';
import { Permission } from '../types/user.types';
import { contextoDaTela } from '../utils/contextoSolicitacao';
import Button from './ui/Button';

type Props = {
  /** Título legível da tela atual. */
  tituloTela: string;
  /** Aparência do botão, para combinar com os demais botões do cabeçalho de cada tela. */
  variant?: 'primary' | 'secondary';
  size?: 'sm' | 'md';
  pill?: boolean;
};

/**
 * Botão "Fale com a equipe" dos detalhes de emenda, plano e pré-projeto. Ao clicar, identifica pela rota a que emenda,
 * plano ou pré-projeto a tela se refere; o servidor valida tudo isso (o que vem do navegador é só informação). Só aparece para quem tem a permissão de abrir solicitações.
 */
const NovaSolicitacaoButton: React.FC<Props> = ({ tituloTela, variant = 'primary', size = 'md', pill = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [enviado, setEnviado] = useState<{ solicitacao: Solicitacao; falhas: string[] } | null>(null);
  const [aberto, setAberto] = useState(false);
  const [contexto, setContexto] = useState(() => contextoDaTela(location.pathname, location.search, tituloTela));

  const fechar = () => {
    setAberto(false);
    setEnviado(null);
  };

  useEffect(() => {
    if (!aberto) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') fechar();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [aberto]);

  const abrir = () => {
    setContexto(contextoDaTela(location.pathname, location.search, tituloTela));
    setAberto(true);
  };

  return (
    <PermissionGate permissions={[Permission.CREATE_SOLICITACAO_ALTERACAO]}>
      <Button variant={variant} size={size} pill={pill} onClick={abrir} title="Pedir uma alteração ou tirar uma dúvida">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5" aria-hidden="true">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        Fale com a equipe
      </Button>

      {aberto && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) fechar();
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Fale com a equipe"
            className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-slate-200 bg-white p-5 shadow-xl dark:border-slate-700 dark:bg-slate-900 sm:p-6"
          >
            {enviado ? (
              <PedidoEnviado
                solicitacao={enviado.solicitacao}
                falhasDeAnexo={enviado.falhas}
                onFechar={fechar}
                onVerPedido={() => {
                  const id = enviado.solicitacao.id;
                  fechar();
                  navigate(`/dashboard/solicitacoes/${id}`);
                }}
              />
            ) : (
              <>
                <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Fale com a equipe</h2>
                <p className="mb-5 mt-1 text-base text-slate-600 dark:text-slate-300">
                  Conte o que você precisa. Nada muda sozinho: a equipe vai ler e responder.
                </p>
                <SolicitacaoForm
                  contexto={contexto}
                  onCancelar={fechar}
                  onCriada={(criada, falhas) => setEnviado({ solicitacao: criada, falhas })}
                />
              </>
            )}
          </div>
        </div>
      )}
    </PermissionGate>
  );
};

export default NovaSolicitacaoButton;
